import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calculator, ShoppingBag, Music2, Store, Settings2, RotateCcw,
  Save, PackageSearch, ArrowLeft, Plus, Trash2, Package, Layers,
} from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatRp, parseNumber, formatThousands } from '../lib/format';
import {
  createMarketplaceProduct, updateMarketplaceProduct, getMarketplaceProduct,
  listMarketplaceBrands, getMarketplaceSettings, saveMarketplaceSettings,
} from '../lib/api';
import { MP_KEYS, FEE_DEFAULTS, ADDON_MASTER_DEFAULTS, mpCalc } from '../lib/marketplaceCalc';
import { MarketplaceSimulator } from '../components/MarketplaceSimulator';

const META = {
  shopee: { label: 'Shopee',     icon: ShoppingBag, chip: 'bg-orange-100 text-orange-800' },
  tiktok: { label: 'TikTok Shop', icon: Music2,     chip: 'bg-slate-100 text-slate-800' },
  minimo: { label: 'Minimo',     icon: Store,       chip: 'bg-blue-100 text-blue-800' },
};
export const PRODUCT_TYPES = [
  { value: 'raket',       label: 'Raket' },
  { value: 'senar',       label: 'Senar' },
  { value: 'grip',        label: 'Grip' },
  { value: 'shuttlecock', label: 'Shuttlecock' },
  { value: 'baju',        label: 'Baju' },
  { value: 'celana',      label: 'Celana' },
  { value: 'aksesoris',   label: 'Aksesoris' },
  { value: 'lainnya',     label: 'Lainnya' },
];

const CUSTOM_TYPES_LS = 'kalkulator_custom_types_v1';
const BUILTIN_VALUES = new Set(PRODUCT_TYPES.map((t) => t.value));

const slugifyType = (s) =>
  String(s || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 32);

export const loadCustomTypes = () => {
  try {
    const raw = localStorage.getItem(CUSTOM_TYPES_LS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((x) => ({ value: String(x.value || '').trim(), label: String(x.label || '').trim() }))
      .filter((x) => x.value && x.label && !BUILTIN_VALUES.has(x.value));
  } catch { return []; }
};

export const saveCustomTypes = (arr) => {
  try { localStorage.setItem(CUSTOM_TYPES_LS, JSON.stringify(arr || [])); } catch { /* ignore */ }
};

export const getAllProductTypes = () => {
  const custom = loadCustomTypes();
  // insert custom types sebelum 'lainnya' agar 'Lainnya' tetap paling bawah
  const idx = PRODUCT_TYPES.findIndex((t) => t.value === 'lainnya');
  const base = [...PRODUCT_TYPES];
  if (idx >= 0) base.splice(idx, 0, ...custom); else base.push(...custom);
  return base;
};

const LS_KEY = 'kalkulator_marketplace_v3';
const defaultConfig = () => ({
  fees: Object.fromEntries(MP_KEYS.map((k) => [k, { ...FEE_DEFAULTS[k] }])),
  addon_master: ADDON_MASTER_DEFAULTS.map((a) => ({ ...a })),
});

// Legacy: tarif dulu disimpan di browser — dipakai sekali untuk migrasi ke server
const loadLegacyLocal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    const fees = {};
    for (const k of MP_KEYS) {
      const f = p?.fees?.[k] || {};
      fees[k] = {
        proses:     Number(f.proses)     || FEE_DEFAULTS[k].proses,
        admin_pct:  Number(f.admin_pct)  || FEE_DEFAULTS[k].admin_pct,
        admin_max:  Number(f.admin_max)  || FEE_DEFAULTS[k].admin_max,
        ongkir_pct: Number.isFinite(Number(f.ongkir_pct)) ? Number(f.ongkir_pct) : FEE_DEFAULTS[k].ongkir_pct,
        ongkir_max: Number.isFinite(Number(f.ongkir_max)) ? Number(f.ongkir_max) : FEE_DEFAULTS[k].ongkir_max,
        pph_pct:    FEE_DEFAULTS[k].pph_pct,
      };
    }
    const master = Array.isArray(p?.addon_master) && p.addon_master.length
      ? p.addon_master.map((a) => ({ name: String(a.name || ''), price: Number(a.price) || 0 }))
      : ADDON_MASTER_DEFAULTS.map((a) => ({ ...a }));
    return { fees, addon_master: master };
  } catch { return null; }
};

const uid = () => `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const emptyVariant = (name = 'Raket Saja', addon_cost = 0) => ({
  id: uid(), name, addon_cost,
  shopee_price: '', tiktok_price: '', minimo_price: '',
});
const standarVariant = () => ({
  id: uid(), name: 'Standar', addon_cost: 0,
  shopee_price: '', tiktok_price: '', minimo_price: '',
});

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get('edit') || '';

  const [productType, setProductType] = useState('raket');
  const [customTypes, setCustomTypes] = useState(() => loadCustomTypes());
  const allTypes = useMemo(() => {
    const idx = PRODUCT_TYPES.findIndex((t) => t.value === 'lainnya');
    const base = [...PRODUCT_TYPES];
    if (idx >= 0) base.splice(idx, 0, ...customTypes); else base.push(...customTypes);
    return base;
  }, [customTypes]);
  const [hasVariants, setHasVariants] = useState(true);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);
  const [baseCost, setBaseCost] = useState('');
  const [variants, setVariants] = useState([emptyVariant()]);
  const [config, setConfig] = useState(defaultConfig);
  const [savedConfig, setSavedConfig] = useState(defaultConfig);
  const [settingsMeta, setSettingsMeta] = useState({ version: 0, updated_at: '', updated_by: '' });
  const [openCfg, setOpenCfg] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [useCurrent, setUseCurrent] = useState(false);
  const [sim, setSim] = useState(null);

  const applySettings = (s) => {
    const c = { fees: s.fees, addon_master: s.addon_master || [] };
    setConfig(c); setSavedConfig(c);
    setSettingsMeta({ version: s.version || 0, updated_at: s.updated_at || '', updated_by: s.updated_by || '' });
  };

  useEffect(() => {
    (async () => {
      try {
        let s = await getMarketplaceSettings();
        const legacy = loadLegacyLocal();
        if (!s.version && legacy) {
          s = await saveMarketplaceSettings(legacy);
          localStorage.removeItem(LS_KEY);
        }
        applySettings(s);
      } catch { toast.error('Gagal memuat tarif marketplace'); }
    })();
  }, []);

  // Load existing brands for autocomplete
  useEffect(() => {
    listMarketplaceBrands().then(setBrandOptions).catch(() => setBrandOptions([]));
  }, []);

  // Cmd/Ctrl + S untuk simpan produk cepat
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        saveProduct();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    if (!editingId) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getMarketplaceProduct(editingId);
        if (cancelled) return;
        const fmt = (n) => n ? new Intl.NumberFormat('id-ID').format(n) : '';
        setName(p.name || '');
        setBrand(p.brand || '');
        setProductType(p.product_type || 'raket');
        setHasVariants(!!p.has_variants || (p.variants || []).length > 1);
        setBaseCost(fmt(p.base_cost || p.modal || 0));
        const vs = (p.variants || []).map((v) => ({
          id: v.id || uid(),
          name: v.name || 'Standar',
          addon_cost: Number(v.addon_cost) || 0,
          shopee_price: fmt(v.shopee_selling_price || 0),
          tiktok_price: fmt(v.tiktok_selling_price || 0),
          minimo_price: fmt(v.minimo_selling_price || 0),
        }));
        setVariants(vs.length ? vs : [standarVariant()]);
        setSnapshot({ fees: p.fee_snapshot, legacy: !!p.snapshot_legacy, version: p.settings_version || 0 });
        setUseCurrent(false);
      } catch {
        toast.error('Gagal memuat produk untuk edit');
        navigate('/kalkulator');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [editingId]);

  // Product type changes reset variant structure
  const changeProductType = (pt) => {
    setProductType(pt);
    if (pt === 'raket') {
      setHasVariants(true);
      if (variants.length === 1 && variants[0].name === 'Standar') {
        setVariants([emptyVariant('Raket Saja', 0)]);
      }
    } else {
      setHasVariants(false);
      // Preserve first variant's prices when switching to simple
      const v0 = variants[0] || standarVariant();
      setVariants([{ ...v0, name: 'Standar', addon_cost: 0 }]);
    }
  };

  const baseCostNum = Number(parseNumber(baseCost) || 0);
  const isSimple = !hasVariants;

  // Produk lama memakai snapshot tarif saat dibuat, kecuali user memilih tarif terbaru
  const activeFees = (editingId && snapshot?.fees && !useCurrent) ? snapshot.fees : savedConfig.fees;

  const computeForVariant = (v) => {
    const modalV = (isSimple ? baseCostNum : baseCostNum + Number(v.addon_cost || 0));
    const hargaByKey = {
      shopee: Number(parseNumber(v.shopee_price) || 0),
      tiktok: Number(parseNumber(v.tiktok_price) || 0),
      minimo: Number(parseNumber(v.minimo_price) || 0),
    };
    const results = Object.fromEntries(MP_KEYS.map((k) => [k, mpCalc(hargaByKey[k], modalV, activeFees[k])]));
    return { modal: modalV, hargaByKey, results };
  };

  const variantsComputed = useMemo(
    () => variants.map((v) => ({ ...v, ...computeForVariant(v) })),
    // eslint-disable-next-line
    [variants, baseCostNum, activeFees, isSimple]
  );

  const openSim = (v, k, tab = 'sim') => setSim({
    variantId: v.id, marketplace: k, harga: v.hargaByKey[k], hpp: v.modal, tab,
    label: hasVariants ? v.name : name || 'Produk',
  });
  const applyRecommended = (mp, price) => {
    if (sim?.variantId) updateVariant(sim.variantId, { [`${mp}_price`]: formatThousands(String(price)) });
    setSim(null);
    toast.success(`Harga ${META[mp].label} diisi ${formatRp(price)}`);
  };

  const anyValid = variantsComputed.some((v) =>
    v.hargaByKey.shopee > 0 || v.hargaByKey.tiktok > 0 || v.hargaByKey.minimo > 0
  );
  const canSave = name.trim().length > 0 && anyValid && !saving;

  const updateVariant = (id, patch) => {
    setVariants((vs) => vs.map((v) => v.id === id ? { ...v, ...patch } : v));
  };
  const addVariant = (preset) => {
    const nm = preset?.name || 'Paket Baru';
    const cost = Number(preset?.price) || 0;
    setVariants((vs) => [...vs, emptyVariant(nm, cost)]);
    if (!hasVariants) setHasVariants(true);
  };
  const removeVariant = (id) => {
    setVariants((vs) => vs.length <= 1 ? vs : vs.filter((v) => v.id !== id));
  };
  const samakanHarga = (id) => {
    const v = variants.find((x) => x.id === id);
    if (!v) return;
    const src = v.shopee_price || v.tiktok_price || v.minimo_price;
    if (!src) { toast.error('Isi salah satu harga dulu'); return; }
    updateVariant(id, { shopee_price: src, tiktok_price: src, minimo_price: src });
  };
  const toggleHasVariants = (v) => {
    setHasVariants(v);
    if (!v) {
      const v0 = variants[0] || standarVariant();
      setVariants([{ ...v0, name: 'Standar', addon_cost: 0 }]);
    } else if (variants.length === 1) {
      setVariants([{ ...variants[0], name: variants[0].name === 'Standar' ? 'Varian 1' : variants[0].name }]);
    }
  };

  const reset = () => {
    setName(''); setBrand(''); setBaseCost('');
    setProductType('raket'); setHasVariants(true);
    setVariants([emptyVariant()]);
  };

  const updateFee = (mpKey, field, value) => {
    setConfig((c) => ({ ...c, fees: { ...c.fees, [mpKey]: { ...c.fees[mpKey], [field]: value } } }));
  };
  const resetConfig = () => {
    if (!window.confirm('Isi ulang form dengan tarif default? (belum tersimpan sampai klik Simpan)')) return;
    setConfig(defaultConfig());
  };
  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const s = await saveMarketplaceSettings(config);
      applySettings(s);
      setOpenCfg(false);
      toast.success(`Tarif tersimpan (versi ${s.version}). Berlaku untuk produk baru.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan tarif');
    } finally { setSavingCfg(false); }
  };
  const updateAddonMaster = (idx, patch) => {
    setConfig((c) => ({ ...c, addon_master: c.addon_master.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  };
  const addAddonMaster = () => setConfig((c) => ({ ...c, addon_master: [...c.addon_master, { name: 'Paket Baru', price: 0 }] }));
  const removeAddonMaster = (idx) => setConfig((c) => ({ ...c, addon_master: c.addon_master.filter((_, i) => i !== idx) }));

  const buildResult = (r) => ({
    profit: r.profit, margin: r.margin, proses: r.proses, admin: r.admin, ongkir: r.ongkir,
    pph: r.pph, bruto: r.bruto, hpp: r.hpp, total_potongan: r.total_potongan, neto: r.neto,
  });

  const saveProduct = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        brand: (brand || '').trim(),
        product_type: productType,
        has_variants: hasVariants,
        base_cost: baseCostNum,
        use_current_rates: !!(editingId && useCurrent),
        variants: variantsComputed.map((v) => ({
          id: v.id,
          name: (v.name || 'Paket').trim() || 'Paket',
          addon_cost: isSimple ? 0 : Number(v.addon_cost) || 0,
          shopee_selling_price: v.hargaByKey.shopee,
          tiktok_selling_price: v.hargaByKey.tiktok,
          minimo_selling_price: v.hargaByKey.minimo,
          shopee: buildResult(v.results.shopee),
          tiktok: buildResult(v.results.tiktok),
          minimo: buildResult(v.results.minimo),
        })),
      };
      if (editingId) {
        await updateMarketplaceProduct(editingId, payload);
        toast.success('Perubahan produk tersimpan');
      } else {
        await createMarketplaceProduct(payload);
        toast.success('Produk tersimpan');
      }
      navigate('/produk-marketplace');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan produk');
    } finally { setSaving(false); }
  };

  const modalLabel = productType === 'raket' ? 'Modal Dasar Raket (Rp)' : 'Modal (Rp)';

  return (
    <AccountingLayout fullWidth>
      <div className="space-y-6" data-testid="calc-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {editingId && (
              <Button variant="outline" size="sm" onClick={() => navigate('/produk-marketplace')}
                className="mr-1 gap-1" data-testid="btn-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="w-9 h-9 rounded-lg bg-emerald-800/10 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-emerald-800" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
                {editingId ? 'Edit Produk' : 'Kalkulator Marketplace'}
              </h1>
              <p className="text-sm text-stone-500">
                Kalkulator profit universal untuk raket, senar, grip &amp; produk lain.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/produk-marketplace')}
              className="gap-1.5" data-testid="btn-daftar-produk">
              <PackageSearch className="w-4 h-4" /> Daftar Produk
            </Button>
            <Button variant="outline" data-testid="btn-open-simulator" className="gap-1.5"
              onClick={() => setSim({ marketplace: 'shopee', harga: 0, hpp: baseCostNum, tab: 'sim' })}>
              <Calculator className="w-4 h-4" /> Simulasi
            </Button>
            <Button variant="outline" onClick={() => { setConfig(savedConfig); setOpenCfg(true); }}
              data-testid="btn-open-config" className="gap-1.5">
              <Settings2 className="w-4 h-4" /> Atur Tarif
            </Button>
          </div>
        </div>

        <RateBanner editing={!!editingId} snapshot={snapshot} useCurrent={useCurrent}
          setUseCurrent={setUseCurrent} meta={settingsMeta} fees={activeFees} />

        {/* PRODUCT META */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-stone-600 mb-1 block">Jenis Produk</Label>
              <Select
                value={productType}
                onValueChange={(v) => {
                  if (v === '__add_type__') {
                    const raw = window.prompt('Nama jenis produk baru:');
                    const label = (raw || '').trim();
                    if (!label) return;
                    let value = slugifyType(label);
                    if (!value) { toast.error('Nama tidak valid'); return; }
                    const existAll = [...allTypes];
                    if (existAll.some((t) => t.value === value)) {
                      toast.error('Jenis dengan nama tersebut sudah ada');
                      return;
                    }
                    const next = [...customTypes, { value, label }];
                    setCustomTypes(next);
                    saveCustomTypes(next);
                    changeProductType(value);
                    toast.success(`Jenis "${label}" ditambahkan`);
                    return;
                  }
                  changeProductType(v);
                }}
              >
                <SelectTrigger className="h-10" data-testid="select-product-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`option-type-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="__add_type__"
                    className="text-emerald-800 font-semibold"
                    data-testid="option-type-add"
                  >
                    + Tambah Jenis Baru
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1 block">Merek</Label>
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                list="brand-options"
                placeholder="Yonex, Lining, Victor…"
                className="h-10"
                data-testid="input-brand"
              />
              <datalist id="brand-options">
                {brandOptions.map((b) => (<option key={b} value={b} />))}
              </datalist>
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1 block">
                Nama Produk <span className="text-red-600">*</span>
              </Label>
              <Input value={name} onChange={(e) => setName(e.target.value.toUpperCase())}
                placeholder={productType === 'raket' ? 'Contoh: ASTROX 77 TOUR' : 'Contoh: BG 66 Ultimax'}
                className="h-10" data-testid="input-name" />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1 block">{modalLabel}</Label>
              <Input value={baseCost}
                onChange={(e) => setBaseCost(formatThousands(e.target.value))}
                inputMode="numeric" placeholder="0"
                className="h-10 text-right font-mono text-lg" data-testid="input-base-cost" />
            </div>
          </div>
          {productType !== 'raket' && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-has-variants">
              <Checkbox checked={hasVariants} onCheckedChange={(v) => toggleHasVariants(!!v)} />
              <span className="text-xs text-stone-600">
                Produk ini memiliki paket/varian (mis. 1 pcs, 5 pcs, 1 roll)
              </span>
            </label>
          )}
        </div>

        {/* VARIANTS UI */}
        {hasVariants ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-800" />
                <div className="text-sm font-semibold text-stone-800">
                  {productType === 'raket' ? 'Paket' : 'Varian'} ({variants.length})
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {productType === 'raket' && config.addon_master.map((a, i) => (
                  <button key={i} type="button" onClick={() => addVariant(a)}
                    className="text-[11px] px-2 py-1 rounded border border-stone-200 bg-white hover:bg-stone-50 hover:border-emerald-300 text-stone-700"
                    data-testid={`btn-preset-${i}`}>
                    + {a.name} {a.price > 0 && <span className="text-stone-400">({formatRp(a.price)})</span>}
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={() => addVariant()}
                  className="h-7 gap-1" data-testid="btn-tambah-paket">
                  <Plus className="w-3.5 h-3.5" /> {productType === 'raket' ? 'Paket Kosong' : 'Varian Baru'}
                </Button>
              </div>
            </div>

            {variantsComputed.map((v) => (
              <VariantCard key={v.id} variant={v} baseCost={baseCostNum}
                showAddon={productType === 'raket'}
                onChange={(patch) => updateVariant(v.id, patch)}
                onRemove={() => removeVariant(v.id)}
                onSamakan={() => samakanHarga(v.id)}
                onSimulate={(k, tab) => openSim(v, k, tab)}
                canRemove={variants.length > 1} />
            ))}
          </div>
        ) : (
          <SimpleProduct
            variant={variantsComputed[0]}
            modal={baseCostNum}
            onChange={(patch) => updateVariant(variantsComputed[0].id, patch)}
            onSamakan={() => samakanHarga(variantsComputed[0].id)}
            onSimulate={(k, tab) => openSim(variantsComputed[0], k, tab)}
          />
        )}

        {/* SAVE ACTIONS */}
        <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap sticky bottom-4 shadow-sm">
          <div className="text-xs text-stone-500">
            {canSave
              ? <>Siap disimpan: <b className="text-stone-800">{name}</b> · {PRODUCT_TYPES.find((t) => t.value === productType)?.label}
                  {hasVariants && ` · ${variants.length} paket`}</>
              : <>Isi <b>Nama Produk</b> &amp; minimal 1 harga marketplace untuk menyimpan.</>}
          </div>
          <div className="flex gap-2">
            {!editingId && (
              <Button variant="outline" size="sm" onClick={reset} data-testid="btn-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
              </Button>
            )}
            <Button onClick={saveProduct} disabled={!canSave}
              className="bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5 disabled:opacity-50"
              data-testid="btn-simpan-produk">
              <Save className="w-4 h-4" />
              {saving ? 'Menyimpan…' : (editingId ? 'Simpan Perubahan' : 'Simpan Produk')}
              <span className="ml-2 hidden sm:inline text-[10px] font-normal opacity-70">⌘S</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Config Dialog */}
      <Dialog open={openCfg} onOpenChange={setOpenCfg}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle>Atur Tarif Marketplace &amp; Master Paket</DialogTitle>
            <DialogDescription>
              Tarif disimpan di server (versi {settingsMeta.version}{settingsMeta.updated_by ? ` · oleh ${settingsMeta.updated_by}` : ''}). Perubahan hanya berlaku untuk produk baru; produk lama tetap memakai snapshot tarif saat dibuat.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {MP_KEYS.map((key) => {
              const f = config.fees[key];
              return (
                <div key={key} className="border border-stone-200 rounded-lg p-4" data-testid={`cfg-${key}`}>
                  <div className="text-sm font-semibold text-stone-800 mb-3">{META[key].label}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <NumField label="Biaya Proses Pesanan (Rp)" value={f.proses} testid={`cfg-${key}-proses`}
                      onChange={(v) => updateFee(key, 'proses', v)} />
                    <NumField label="Admin Marketplace (%)" value={f.admin_pct} step="0.1" testid={`cfg-${key}-admin_pct`}
                      onChange={(v) => updateFee(key, 'admin_pct', v)} />
                    <NumField label="Maks. Biaya Admin (Rp)" value={f.admin_max} testid={`cfg-${key}-admin_max`}
                      onChange={(v) => updateFee(key, 'admin_max', v)} />
                    <NumField label="Gratis / Subsidi Ongkir (%)" value={f.ongkir_pct} step="0.1" testid={`cfg-${key}-ongkir_pct`}
                      onChange={(v) => updateFee(key, 'ongkir_pct', v)} />
                    <NumField label="Maks. Subsidi Ongkir (Rp)" value={f.ongkir_max} testid={`cfg-${key}-ongkir_max`}
                      onChange={(v) => updateFee(key, 'ongkir_max', v)} />
                    <NumField label="Pajak PPh Final (%)" value={f.pph_pct} step="0.1" testid={`cfg-${key}-pph_pct`}
                      onChange={(v) => updateFee(key, 'pph_pct', v)} />
                  </div>
                  <div className="text-[11px] text-stone-500 mt-2">
                    PPh dihitung dari omzet bruto, terpisah dari admin. Maks. 0 = tanpa batas.
                  </div>
                </div>
              );
            })}
            <div className="border border-stone-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-stone-800">Master Paket / Add-on Raket</div>
                <Button size="sm" variant="outline" onClick={addAddonMaster} className="h-7 gap-1">
                  <Plus className="w-3.5 h-3.5" /> Tambah
                </Button>
              </div>
              <div className="space-y-2">
                {config.addon_master.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={a.name} onChange={(e) => updateAddonMaster(i, { name: e.target.value })}
                      className="h-9 flex-1" placeholder="Nama paket" />
                    <Input type="number" value={a.price}
                      onChange={(e) => updateAddonMaster(i, { price: Number(e.target.value) || 0 })}
                      className="h-9 w-32 text-right font-mono" placeholder="0" />
                    <button onClick={() => removeAddonMaster(i)}
                      className="p-1.5 rounded border border-red-200 text-red-700 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="justify-between sm:justify-between">
            <Button variant="ghost" onClick={resetConfig} data-testid="btn-reset-config">
              Kembalikan Default
            </Button>
            <Button onClick={saveConfig} disabled={savingCfg}
              className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="btn-save-config">
              {savingCfg ? 'Menyimpan…' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {sim && (
        <MarketplaceSimulator open={!!sim} onOpenChange={(o) => { if (!o) setSim(null); }}
          fees={activeFees} init={sim} onApply={sim.variantId ? applyRecommended : undefined} />
      )}
    </AccountingLayout>
  );
}

const RateBanner = ({ editing, snapshot, useCurrent, setUseCurrent, meta, fees }) => {
  const pph = MP_KEYS.map((k) => `${META[k].label} ${fees[k].pph_pct}%`).join(' · ');
  const onSnapshot = editing && snapshot && !useCurrent;
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 flex-wrap text-xs ${onSnapshot ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50/60 text-emerald-900'}`}
      data-testid="rate-banner">
      <div>
        {onSnapshot
          ? <>Memakai <b>snapshot tarif produk</b>{snapshot.legacy ? ' (produk lama, tanpa PPh)' : ` (versi ${snapshot.version})`}.</>
          : <>Memakai <b>tarif terbaru</b> (versi {meta.version}).</>}
        <span className="ml-2 opacity-80">PPh Final: {pph}</span>
      </div>
      {editing && snapshot && (
        <button type="button" onClick={() => setUseCurrent(!useCurrent)}
          className="font-semibold underline underline-offset-2" data-testid="btn-toggle-rates">
          {useCurrent ? 'Kembali ke snapshot' : 'Pakai tarif terbaru untuk produk ini'}
        </button>
      )}
    </div>
  );
};

// Simple product view (non-raket without variants)
const SimpleProduct = ({ variant: v, modal, onChange, onSamakan, onSimulate }) => (
  <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
    <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4 text-emerald-800" />
        <div className="text-sm font-semibold text-stone-800">Harga Jual per Marketplace</div>
      </div>
      <button onClick={onSamakan}
        className="text-[11px] text-emerald-800 hover:text-emerald-900 font-medium underline underline-offset-2"
        data-testid="btn-samakan-harga">
        Samakan Harga
      </button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4">
      {MP_KEYS.map((k) => (
        <MarketplaceProfitCard key={k} k={k} variant={v} modal={modal}
          onChange={(patch) => onChange(patch)} onSimulate={(tab) => onSimulate(k, tab)} />
      ))}
    </div>
  </div>
);

const MarketplaceProfitCard = ({ k, variant: v, modal, onChange, onSimulate }) => {
  const meta = META[k];
  const Icon = meta.icon;
  const harga = v.hargaByKey[k];
  const r = v.results[k];
  const positive = r.profit >= 0;
  const hasInput = harga > 0;
  const profitColor = !hasInput ? 'text-stone-400' : (positive ? 'text-emerald-900' : 'text-red-900');
  const testBase = `${k}-${v.id}`;
  return (
    <div className="rounded-lg border border-stone-200 overflow-hidden flex flex-col">
      <div className={`px-3 py-2 flex items-center gap-2 border-b ${meta.chip}`}>
        <Icon className="w-3.5 h-3.5" />
        <div className="text-xs font-semibold flex-1">{meta.label}</div>
        <button type="button" onClick={() => onSimulate('rec')} title="Harga rekomendasi"
          className="text-[10px] font-semibold underline underline-offset-2 opacity-80 hover:opacity-100"
          data-testid={`btn-rec-${testBase}`}>Rekomendasi</button>
        <button type="button" onClick={() => onSimulate('sim')} title="Simulasi rinci"
          className="p-0.5 rounded hover:bg-black/5" data-testid={`btn-sim-${testBase}`}>
          <Calculator className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-3 space-y-2 flex-1">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Harga Jual</Label>
          <Input value={v[`${k}_price`]}
            onChange={(e) => onChange({ [`${k}_price`]: formatThousands(e.target.value) })}
            inputMode="numeric" placeholder="0"
            className="h-9 text-right font-mono text-sm"
            data-testid={`simple-price-${k}-${v.id}`} />
        </div>
        {hasInput && (
          <div className="text-[10px] text-stone-500 space-y-0.5 pt-1" data-testid={`breakdown-${testBase}`}>
            <div className="flex justify-between"><span>Modal (HPP)</span><span className="font-mono text-red-700">−{formatRp(modal)}</span></div>
            <div className="flex justify-between"><span>Admin</span><span className="font-mono text-red-700">−{formatRp(r.admin)}</span></div>
            {r.ongkir > 0 && <div className="flex justify-between"><span>Gratis ongkir</span><span className="font-mono text-red-700">−{formatRp(r.ongkir)}</span></div>}
            <div className="flex justify-between"><span>Proses pesanan</span><span className="font-mono text-red-700">−{formatRp(r.proses)}</span></div>
            {r.pph > 0 && <div className="flex justify-between"><span>PPh Final</span><span className="font-mono text-red-700" data-testid={`pph-${testBase}`}>−{formatRp(r.pph)}</span></div>}
            <div className="flex justify-between border-t border-stone-200 pt-0.5 text-stone-700"><span>Dana bersih diterima</span><span className="font-mono" data-testid={`neto-${testBase}`}>{formatRp(r.neto)}</span></div>
          </div>
        )}
        <div className={`rounded-md px-2 py-1.5 mt-auto ${!hasInput ? 'bg-stone-50 border border-stone-200' : (positive ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200')}`}>
          <div className="text-[9px] uppercase tracking-widest font-semibold text-stone-600">Profit</div>
          <div className={`text-base font-bold font-mono ${profitColor}`}
            data-testid={`simple-profit-${k}`}>
            {hasInput ? formatRp(r.profit) : '—'}
          </div>
          {hasInput && (
            <div className={`text-[10px] font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
              {r.margin.toFixed(1)}% margin
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const VariantCard = ({ variant: v, baseCost, showAddon, onChange, onRemove, onSamakan, onSimulate, canRemove }) => {
  const totalModal = baseCost + Number(v.addon_cost || 0);
  return (    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden"
      data-testid={`variant-${v.id}`}>
      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">
            {showAddon ? 'Nama Paket' : 'Nama Varian'}
          </Label>
          <Input value={v.name} onChange={(e) => onChange({ name: e.target.value })}
            className="h-8 text-sm font-semibold" data-testid={`variant-name-${v.id}`} />
        </div>
        {showAddon && (
          <div className="w-40">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">
              Tambahan Modal (Rp)
            </Label>
            <Input value={v.addon_cost ? new Intl.NumberFormat('id-ID').format(v.addon_cost) : ''}
              onChange={(e) => onChange({ addon_cost: Number(parseNumber(e.target.value) || 0) })}
              inputMode="numeric" placeholder="0"
              className="h-8 text-right font-mono text-sm" data-testid={`variant-addon-${v.id}`} />
          </div>
        )}
        <div className="text-right" title={`HPP raket ${formatRp(baseCost)} + tambahan ${formatRp(v.addon_cost || 0)}`}>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Total HPP Paket</div>
          <div className="text-sm font-mono font-bold text-stone-900" data-testid={`variant-total-hpp-${v.id}`}>{formatRp(totalModal)}</div>
          {showAddon && <div className="text-[10px] text-stone-400 font-mono">{formatRp(baseCost)} + {formatRp(v.addon_cost || 0)}</div>}
        </div>
        <button onClick={onSamakan}
          className="text-[11px] text-emerald-800 hover:text-emerald-900 font-medium underline underline-offset-2">
          Samakan Harga
        </button>
        {canRemove && (
          <button onClick={onRemove}
            className="p-1.5 rounded border border-red-200 text-red-700 hover:bg-red-50"
            data-testid={`variant-remove-${v.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4">
        {MP_KEYS.map((k) => (
          <MarketplaceProfitCard key={k} k={k} variant={v} modal={totalModal}
            onChange={(patch) => onChange(patch)} onSimulate={(tab) => onSimulate(k, tab)} />
        ))}
      </div>
    </div>
  );
};

const NumField = ({ label, value, onChange, step = '1', testid }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{label}</Label>
    <Input type="number" step={step} min="0" value={value}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      className="h-9 text-right font-mono text-sm" data-testid={testid} />
  </div>
);
